import MainButton from '@/components/ui/custom/buttons/MainButton';
import ComponentGuard from '@/components/ui/custom/ComponentGuard';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import ToggleGroup, {
  ToggleButton,
} from '@/components/ui/custom/dataInput/ToggleGroup';
import Filter from '@/components/filter/Filter';
import useStudentContext from '@/data/hook/useStudentContext';
import useAuth from '@/data/hook/useAuth';
import { ProjectFilters } from '@/data/services/projectServices';
import { isStaff } from '@/functions/auth';
import { UserTypeEnum } from '@/models/User';
import { createMultipleToolFilter } from '@/utils/filterFactory';
import { RectangleStackIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

type ProjectFilterEnum = 'all' | 'myschool' | 'myklass' | 'mycourse';

export type ProjectFiltersFields = {
  restrict?: ProjectFilterEnum;
} & Pick<ProjectFilters, 'search' | 'toolsId'>;

type ProjectPageFiltersProps = {
  filters: ProjectFiltersFields;
  onFilter: (filters: ProjectFiltersFields) => void;
};

export const ProjectPageFilters = ({
  filters,
  onFilter,
}: ProjectPageFiltersProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'projects.page' });
  const { validEnrollment } = useStudentContext();

  const navigate = useNavigate();
  const { user } = useAuth();
  const buttons: ToggleButton<ProjectFilterEnum>[] = [
    {
      value: 'all',
      text: t('all'),
    },
    {
      value: 'myschool',
      text: t('filters.mySchool'),
    },
    {
      value: 'mycourse',
      text: t('filters.myCourse'),
    },
    {
      value: 'myklass',
      text: t('filters.myClass'),
    },
  ];

  const buttonsAdminClausule = (button: ToggleButton<ProjectFilterEnum>) => {
    return (
      !isStaff(user?.userType) ||
      !['mycourse', 'myklass'].includes(button.value)
    );
  };

  const canceledStudentClausule = (button: ToggleButton<ProjectFilterEnum>) => {
    return validEnrollment || button.value !== 'myklass';
  };

  const buttonsByRole = buttons
    .filter(buttonsAdminClausule)
    .filter(canceledStudentClausule);

  const { pathname } = useLocation();
  return (
    <div className="flex justify-between items-center flex-wrap gap-y-2">
      <div className="flex gap-4 items-center flex-wrap justify-between w-full xs:justify-center xs:w-fit">
        <ConditionalRenderer condition={pathname !== '/projects/me'}>
          <ComponentGuard
            roles={[
              UserTypeEnum.UNIT_ADMIN,
              UserTypeEnum.TEACHER,
              UserTypeEnum.STUDENT,
            ]}
          >
            <ToggleGroup
              buttons={buttonsByRole}
              exclusive
              value={filters.restrict || 'all'}
              onChange={restrict => {
                onFilter({ restrict });
              }}
            />
          </ComponentGuard>
          <ComponentGuard roles={[UserTypeEnum.TEACHER, UserTypeEnum.STUDENT]}>
            <MainButton
              color="custom"
              text={t('filters.myProjects')}
              className="!text-primary font-600 text-14 gap-1 flex-row-reverse whitespace-nowrap"
              icon={<RectangleStackIcon />}
              onClick={() => navigate('/projects/me')}
            />
          </ComponentGuard>
        </ConditionalRenderer>
      </div>
      <Filter
        filter={filters}
        hideQuick
        className={{
          ul: 'gap-2.5 justify-end xs:justify-center',
        }}
        configs={[
          createMultipleToolFilter(),
          {
            type: 'search',
            placeholder: t('filters.searchPlaceholder'),
            name: 'search',
            debounce: true,
          },
        ]}
        onChange={onFilter}
      />
    </div>
  );
};
